import { LoaderCircleIcon, PencilIcon } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { UserAvatar } from "@/components/UserAvatar";
import { selectAvatarUrl, selectDisplayName, useAuthStore } from "@/store/authStore";

export function AvatarSection() {
  const user = useAuthStore(state => state.user);
  const displayName = useAuthStore(selectDisplayName);
  const photoURL = useAuthStore(selectAvatarUrl);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const uploadAvatar = useAuthStore(state => state.uploadAvatar);

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("이미지 파일을 선택해 주세요.", { richColors: true, position: "top-center" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("5MB 이하의 이미지를 선택해 주세요.", { richColors: true, position: "top-center" });
      return;
    }

    if (!user) {
      return;
    }

    setIsUploadingPhoto(true);

    try {
      await uploadAvatar(file);
    } catch {
      toast.error("사진 변경에 실패했어요. 다시 시도해 주세요.", { richColors: true, position: "top-center" });
    } finally {
      setIsUploadingPhoto(false);

      if (photoInputRef.current) {
        photoInputRef.current.value = "";
      }
    }
  }

  return (
    <div className="flex flex-col items-center gap-2 mb-8">
      {/* 프로필 사진 */}
      <button
        type="button"
        className="relative size-20 rounded-full cursor-pointer disabled:pointer-events-none"
        disabled={isUploadingPhoto}
        onClick={() => photoInputRef.current?.click()}
      >
        <UserAvatar src={photoURL} alt={displayName} className="size-full" />
        <span className="absolute bottom-0 right-0 flex size-6 items-center justify-center rounded-full bg-white shadow">
          {isUploadingPhoto ? (
            <LoaderCircleIcon className="size-3.5 animate-spin text-muted-foreground" />
          ) : (
            <PencilIcon className="size-3.5 text-foreground" />
          )}
        </span>
      </button>

      {/* 프로필 사진 업로드 인풋 */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => void handlePhotoChange(e)}
      />
    </div>
  );
}
